import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import GlowCard from "../components/GlowCard";
import { listSections } from "./api";
import { VerityStandard, VeritySection } from "./types";

interface SectionTreeViewProps {
  standard: VerityStandard;
}

/**
 * The document's structure as extracted: every chapter, subsection and appendix, with the pages it
 * spans and the page numbers a citation would quote.
 *
 * Both page numbers are shown deliberately. In the Collingwood standard the printed number trails the
 * PDF number by between 10 and 15 pages depending where you are in the document, because front matter
 * and divider pages are not counted. A citation that quotes the wrong one sends a reviewer to the
 * wrong page, and the offset cannot be computed — it is read off each page's footer.
 */
const SectionTreeView = ({ standard }: SectionTreeViewProps) => {
  const [sections, setSections] = useState<VeritySection[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSections(null);
    setError(null);

    listSections(standard.id)
      .then((rows) => {
        if (!cancelled) setSections(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the section tree.");
      });

    return () => {
      cancelled = true;
    };
  }, [standard.id]);

  const summary = standard.extraction;

  return (
    <GlowCard className="vr-tree-card" hover={false}>
      <div className="vr-tree-head">
        <h3>
          {standard.title} — {standard.edition}
        </h3>
        {summary && (
          <div className="vr-tree-stats">
            <span>
              <b>{summary.pageCount}</b> pages
            </span>
            <span>
              <b>{summary.textPageCount}</b> with text
            </span>
            {/* Named, not buried: these pages hold the standard drawings and no text extraction will
                ever reach them. Coverage that ignores them would be overstated. */}
            <span>
              <b>{summary.rasterPageCount}</b> raster only
            </span>
            <span>
              <b>{summary.chapterCount}</b> chapters
            </span>
            <span>
              <b>{summary.appendixCount}</b> appendices
            </span>
            <span>
              <b>{summary.subsectionCount}</b> subsections
            </span>
            <span>
              <b>{summary.characterCount.toLocaleString()}</b> characters
            </span>
          </div>
        )}
      </div>

      {error && <div className="vr-empty">{error}</div>}

      {!error && sections === null && (
        <div className="vr-empty">
          <Loader2 size={16} className="vr-spin-inline" /> Loading the section tree…
        </div>
      )}

      {!error && sections !== null && sections.length === 0 && (
        <div className="vr-empty">No sections yet — run Extract on this standard.</div>
      )}

      {!error && sections !== null && sections.length > 0 && (
        <div className="vr-tree-body">
          {sections.map((section) => (
            <div
              key={section.sectionId}
              className={`vr-tree-row vr-tree-depth-${Math.min(section.depth, 3)}`}
              title={section.path.join("  ›  ")}
            >
              <span className="vr-tree-label">
                <span className="vr-tree-num">{section.numbering}</span>
                <span className="vr-tree-title">{section.title}</span>
              </span>

              <span className="vr-tree-pages">
                {section.printedPageStart !== null
                  ? `p ${section.printedPageStart}–${section.printedPageEnd}`
                  : "no printed page"}
                <span className="vr-cell-dim">
                  {" "}
                  (pdf {section.pdfPageStart}–{section.pdfPageEnd})
                </span>
              </span>

              <span className="vr-tree-flag">
                {section.rasterPageCount > 0 ? `${section.rasterPageCount} raster` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </GlowCard>
  );
};

export default SectionTreeView;
